import { Auth, API } from "aws-amplify";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { useDispatch, useSelector } from "react-redux";
import { setListing, setSearchText, setSelected } from "../store/ducks/admins";
import { HeadCell } from "../models/dataTable";
import { AdminsApiPaths, AdminsConstants } from "../constants/enums";
import useApp from "./useApp";
import useAccount from "./useAccount";
import { AdminGroup } from "../models";
import { UpdateAdminGroupInput } from "../models/GQL_API";
import {
  listAdminGroupsCustom,
  listAdminGroupsWithVersion,
} from "../graphqlCustom/queries";
import { updateAdminGroup } from "../graphql/mutations";
import useUserActivity from "./useUserActivity";

/** Format an ISO timestamp for display in Cairo time, or "—" when absent. */
function formatActivityTime(iso: any): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    timeZone: "Africa/Cairo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showError, showConfirm, session } = useApp();
  const { accountsGetDomainName } = useAccount("accounts", "account");
  const { userActivityListAll } = useUserActivity();

  const listing = useSelector(
    (state: any) => state[`${listingName}`]["listing"]
  );
  const searchText = useSelector(
    (state: any) => state[`${listingName}`]["searchText"]
  );
  const selected = useSelector(
    (state: any) => state[`${listingName}`]["selected"]
  );
  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]["nextToken"]
  );

  const COGNITO_GROUPS = [{ label: "admin", value: "admin" }];

  // Amplify's API.graphql throws when the response includes any `errors`,
  // even if `data` is populated. listAdminGroupsWithVersion can return
  // partial errors for legacy rows missing `_version` (non-null type
  // violation drops the item to null but the rest of the page is fine).
  // Treat the throw as success when `data` is present.
  async function graphqlTolerant(opts: any): Promise<any> {
    try {
      return await API.graphql(opts);
    } catch (e: any) {
      if (e?.data) {
        console.warn("[useAdmin] graphql returned partial errors", e.errors);
        return e;
      }
      throw e;
    }
  }

  /**
   * Add user to AdminGroup table in DynamoDB
   * @param userSub - The Cognito UserSub (user ID)
   * @param groupName - The AdminGroup name to add the user to
   */
  async function addUserToAdminGroupTable(userSub: string, groupName: string) {
    try {
      // Find the AdminGroup by name
      const filter: any = {
        deleted: { eq: "0" },
        name: { eq: groupName.toLowerCase() },
      };

      const groupsList: any = await graphqlTolerant({
        query: listAdminGroupsWithVersion,
        variables: { filter },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      const groups = groupsList.data.listAdminGroups.items;

      if (groups && groups.length > 0) {
        const adminGroup = groups[0];
        const existingUsers = adminGroup.users || [];

        // Check if user is already in the group
        if (!existingUsers.includes(userSub)) {
          const version = (adminGroup as any)._version;
          const updateInput: any = {
            id: adminGroup.id,
            users: [...existingUsers, userSub],
            ...(typeof version === "number" ? { _version: version } : {}),
          };

          await API.graphql<AdminGroup>({
            query: updateAdminGroup,
            variables: { input: updateInput },
            authMode: session
              ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
              : GRAPHQL_AUTH_MODE.AWS_IAM,
          });

          console.log(`User ${userSub} added to AdminGroup ${groupName}`);
        }
      } else {
        console.warn(`AdminGroup '${groupName}' not found in database`);
      }
    } catch (err: Error | any) {
      console.error("Error adding user to AdminGroup table:", err);
      throw err;
    }
  }

  async function create(data: any) {
    try {
      const domain = (await accountsGetDomainName())?.toString().trim();
      const adminGroupName = data.group ? data.group : "admin";

      // Cognito's username regex [\p{L}\p{M}\p{S}\p{N}\p{P}]+ rejects any
      // whitespace, so trim aggressively. Lowercase the email too — Cognito
      // is case-sensitive on usernames and login uses the lowercased form
      // (see useAuth.login). Without this, "Ahmed.helal@..." would create a
      // different username than "ahmed.helal@..." and confuse logins later.
      const rawEmail = (data.email ?? "").toString().trim();
      const cleanEmail = rawEmail.toLowerCase();
      const cleanName = (data.name ?? "").toString().trim();

      if (!cleanEmail || /\s/.test(cleanEmail)) {
        showError(
          "Email contains whitespace or is empty. Please re-type the email (no spaces)."
        );
        return;
      }
      // RFC-ish sanity check — catches the most common copy-paste mistakes.
      if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(cleanEmail)) {
        showError(`Email "${rawEmail}" is not a valid email address.`);
        return;
      }
      if (!domain) {
        showError(
          "Could not resolve the account domain. Reload and try again."
        );
        return;
      }

      const cognitoUsernameInput = `${domain}_${cleanEmail}`;
      console.log("[createUser] signing up Cognito user", {
        cognitoUsernameInput,
        adminGroupName,
      });

      const user = await Auth.signUp({
        username: cognitoUsernameInput,
        password: data.password,
        attributes: {
          name: cleanName,
          email: cognitoUsernameInput,
        },
      });

      const userSub = user.userSub;
      const cognitoUsername = user.user.getUsername();
      console.log("[createUser] Cognito user created", { userSub, cognitoUsername });

      if (!userSub) {
        throw new Error("Cognito sign-up did not return a userSub");
      }

      // Cognito group is a coarse "is authenticated as admin" flag — every
      // app user lives in the same one so AdminQueries + model @auth rules
      // accept their JWT. Fine-grained UI access is driven by the AdminGroup
      // table (receipts_user / invoices_user / mixed_user / admin / manager).
      await addUserToGroup(cognitoUsername, "admin");
      console.log("[createUser] added to Cognito 'admin' group", cognitoUsername);

      await addUserToAdminGroupTable(userSub, adminGroupName);
      console.log(
        "[createUser] appended userSub to AdminGroup",
        { adminGroupName, userSub }
      );

      showConfirm(
        `User created and added to '${adminGroupName}' group successfully`
      );
    } catch (err: Error | any) {
      console.error("[createUser] failed", err);
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function adminsConfig(apiPath: string, queryString: any, body?: any) {
    let apiName = AdminsConstants.ADMINS_API;
    let path = apiPath;
    let myInit = {
      body: body,
      queryStringParameters: queryString,
      headers: {
        "Content-Type": "application/json",
        Authorization: `${(await Auth.currentSession())
          .getAccessToken()
          .getJwtToken()}`,
      },
    };

    return {
      apiName,
      path,
      myInit,
    };
  }

  async function listGroupsForUser(username: string) {
    let groups: any[] = [];
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.LIST_GROUPS_FOR_USER,
        {
          username,
        }
      );
      const { Groups } = await API.get(apiName, path, myInit);
      for (let group of Groups) {
        groups.push(group["GroupName"]);
      }
      return groups;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function listAllGroups() {
    let groups: any[] = [];
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.LIST_GROUPS,
        { limit: 60 }
      );
      const { Groups } = await API.get(apiName, path, myInit);
      for (let group of Groups) {
        groups.push(group["GroupName"]);
      }
      return groups;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function addUserToGroup(username: string, groupname: string) {
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.ADD_USER_TO_GROUP,
        {},
        {
          username,
          groupname,
        }
      );
      const { ...result } = await API.post(apiName, path, myInit);
      showConfirm(result.message);
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message);
    }
  }

  async function removeUserFromGroup(username: string, groupname: string) {
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.REMOVE_USER_FROM_GROUP,
        {},
        {
          username,
          groupname,
        }
      );
      const { ...result } = await API.post(apiName, path, myInit);
      showConfirm(result.message);
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message);
    }
  }

  async function fetch(nextToken: string, limit: number) {
    try {
      // Filter users by account domain
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.LIST_USERS,
        {
          limit: limit,
          token: nextToken,
        }
      );

      const { NextToken, ...data } = await API.get(apiName, path, myInit);
      nextToken = NextToken === undefined ? "" : NextToken;
      let listing: any[] = [];
      let cognitoObj = data.Users;
      const domain = await accountsGetDomainName();

      for (let user of cognitoObj) {
        // Skip disabled users
        if (user.Enabled === false) {
          continue;
        }
        
        let { Attributes, ...userData } = user;

        for (let attr of user["Attributes"]) {
          userData[attr["Name"]] = attr["Value"];
          if (userData["Username"]) {
            userData["id"] = userData["Username"];
          }
          if (userData["UserCreateDate"]) {
            userData["createdAt"] = userData["UserCreateDate"];
          }
          if (userData["email"]) {
            userData["email"] = userData["email"].split(`${domain}_`)[1];
          }
        }
        // get user groups
        // if (userData["Username"]) {
        //   userData["Groups"] = await listGroupsForUser(userData["Username"]);
        // }
        listing.push(userData);
      }

      // Join last login / last active from the UserActivity table (by sub).
      try {
        const activityBySub = await userActivityListAll(session);
        for (const u of listing) {
          const act = activityBySub[(u.sub || u.id || "").toString()];
          u.lastLoginAt = formatActivityTime(act?.lastLoginAt);
          u.lastActiveAt = formatActivityTime(act?.lastActiveAt);
        }
      } catch (activityErr) {
        // Non-fatal — show the user list even if activity lookup fails.
        console.warn("[useAdmin] activity join failed", activityErr);
        for (const u of listing) {
          u.lastLoginAt = u.lastLoginAt ?? "—";
          u.lastActiveAt = u.lastActiveAt ?? "—";
        }
      }

      dispatch(setListing(listing));
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function byGroup(nextToken: string, limit: number, groupName: string) {
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.LIST_USERS_IN_GROUP,
        {
          groupname: groupName,
          limit: limit,
          token: nextToken,
        }
      );

      const { NextToken, ...data } = await API.get(apiName, path, myInit);
      nextToken = NextToken;
      const listing = data.Users;

      dispatch(setListing(listing));
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function get(username: string) {
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.GET_USER,
        {
          username,
        }
      );

      const { ...single } = await API.get(apiName, path, myInit);
      let { UserAttributes, ...userData } = single;
      const domain = await accountsGetDomainName();

      for (let attr of single["UserAttributes"]) {
        userData[attr["Name"]] = attr["Value"];
        if (userData["Username"]) {
          userData["id"] = userData["Username"];
        }
        if (userData["UserCreateDate"]) {
          userData["createdAt"] = userData["UserCreateDate"];
        }
        if (userData["email"]) {
          userData["email"] = userData["email"].split(`${domain}_`)[1];
        }

        // get user groups
      }
      if (userData["Username"]) {
        userData["Groups"] = await listGroupsForUser(userData["Username"]);
      }
      return userData;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function enableUser(username: string) {
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.ENABLE_USER,
        {},
        {
          username,
        }
      );
      const { ...result } = await API.post(apiName, path, myInit);
      console.log(result.message);

      showConfirm("User enabled successfully.");
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message);
    }
  }

  // Best-effort: scrub the deleted user's sub out of every AdminGroup.users
  // array so dangling references don't accumulate. Looks the user up first
  // to translate Cognito Username → sub (the AdminGroup stores subs, not usernames).
  async function removeUserSubFromAllAdminGroups(username: string) {
    try {
      const userData = await get(username);
      const sub = userData?.sub;
      if (!sub) {
        console.warn(
          `[deleteUser] no sub for ${username}; skipping AdminGroup cleanup`
        );
        return;
      }

      const filter: any = {
        deleted: { eq: "0" },
      };
      const res: any = await graphqlTolerant({
        query: listAdminGroupsWithVersion,
        variables: { filter, limit: 200 },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });
      const groups = (res?.data?.listAdminGroups?.items || []).filter(
        (g: any) => g && g.id
      );

      for (const g of groups) {
        const users: string[] = Array.isArray(g.users) ? g.users : [];
        if (!users.includes(sub)) continue;
        const version = (g as any)._version;
        const updateInput: any = {
          id: g.id,
          users: users.filter((s: string) => s !== sub),
          // Pass _version to avoid AppSync AUTOMERGE union-merging the list
          // (which silently undoes the remove).
          ...(typeof version === "number" ? { _version: version } : {}),
        };
        try {
          await API.graphql<AdminGroup>({
            query: updateAdminGroup,
            variables: { input: updateInput },
            authMode: session
              ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
              : GRAPHQL_AUTH_MODE.AWS_IAM,
          });
          console.log(`[deleteUser] removed ${sub} from AdminGroup '${g.name}'`);
        } catch (err) {
          console.warn(
            `[deleteUser] failed to scrub sub from AdminGroup '${g.name}':`,
            err
          );
        }
      }
    } catch (err) {
      console.warn("[deleteUser] AdminGroup cleanup failed (non-fatal):", err);
    }
  }

  // Update name, optionally password, and optionally move the user to a
  // different AdminGroup. `data` shape: { name?, password?, group? } where
  // `group` is the AdminGroup name (not id).
  async function update(
    username: string,
    data: { name?: string; password?: string; group?: string }
  ) {
    try {
      const cleanName = (data.name ?? "").toString().trim();
      const cleanPassword = (data.password ?? "").toString();
      const newGroupName = (data.group ?? "").toString().trim().toLowerCase();

      // 1) Update name attribute if provided
      if (cleanName) {
        const { apiName, path, myInit } = await adminsConfig(
          AdminsApiPaths.UPDATE_USER_ATTRIBUTES,
          {},
          { username, attributes: { name: cleanName } }
        );
        await API.post(apiName, path, myInit);
        console.log("[updateUser] name updated");
      }

      // 2) Set password if provided
      if (cleanPassword) {
        const { apiName, path, myInit } = await adminsConfig(
          AdminsApiPaths.SET_USER_PASSWORD,
          {},
          { username, password: cleanPassword }
        );
        await API.post(apiName, path, myInit);
        console.log("[updateUser] password updated");
      }

      // 3) Move AdminGroup membership if changed
      if (newGroupName) {
        const userData = await get(username);
        const sub = userData?.sub;
        console.log("[updateUser] move start", {
          username,
          sub,
          targetGroup: newGroupName,
        });

        if (!sub) {
          console.warn("[updateUser] no sub on user, skipping group move");
        } else {
          // Pull every AdminGroup, paginating, so a single-page limit can't
          // hide groups the user is in.
          const allGroups: any[] = [];
          let nextToken: string | undefined;
          do {
            const res: any = await graphqlTolerant({
              query: listAdminGroupsWithVersion,
              variables: {
                filter: { deleted: { eq: "0" } },
                limit: 200,
                nextToken,
              },
              authMode: session
                ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
                : GRAPHQL_AUTH_MODE.AWS_IAM,
            });
            if (res?.errors?.length) {
              console.warn("[updateUser] listAdminGroups partial errors", res.errors);
            }
            const page = res?.data?.listAdminGroups;
            // Records without _version come back as null items — drop them.
            if (page?.items)
              allGroups.push(...page.items.filter((g: any) => g && g.id));
            nextToken = page?.nextToken;
          } while (nextToken);

          console.log(
            "[updateUser] groups containing this sub BEFORE move:",
            allGroups
              .filter(
                (g: any) => Array.isArray(g.users) && g.users.includes(sub)
              )
              .map((g: any) => g.name)
          );

          for (const g of allGroups) {
            const currentUsers: string[] = Array.isArray(g.users)
              ? g.users.filter((u: any) => typeof u === "string")
              : [];
            const isTarget = (g.name || "").toLowerCase() === newGroupName;
            const hasUser = currentUsers.includes(sub);

            // Pass _version so AppSync uses optimistic-concurrency instead
            // of AUTOMERGE. Without this, list fields like `users` get
            // UNION-merged on the server, so removing an item is silently
            // undone (the removed sub reappears).
            const version = (g as any)._version;

            if (isTarget && !hasUser) {
              const nextUsers = [...currentUsers, sub];
              const updateInput: any = {
                id: g.id,
                users: nextUsers,
                ...(typeof version === "number" ? { _version: version } : {}),
              };
              const upd: any = await API.graphql<AdminGroup>({
                query: updateAdminGroup,
                variables: { input: updateInput },
                authMode: session
                  ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
                  : GRAPHQL_AUTH_MODE.AWS_IAM,
              });
              if (upd?.errors?.length) {
                console.error(
                  `[updateUser] FAILED to add sub to '${g.name}'`,
                  upd.errors
                );
                throw new Error(
                  `Could not add user to group '${g.name}': ${upd.errors[0]?.message}`
                );
              }
              console.log(
                `[updateUser] added sub to AdminGroup '${g.name}' (users now ${nextUsers.length}, _version was ${version})`
              );
            } else if (!isTarget && hasUser) {
              const nextUsers = currentUsers.filter((s: string) => s !== sub);
              const updateInput: any = {
                id: g.id,
                users: nextUsers,
                ...(typeof version === "number" ? { _version: version } : {}),
              };
              const upd: any = await API.graphql<AdminGroup>({
                query: updateAdminGroup,
                variables: { input: updateInput },
                authMode: session
                  ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
                  : GRAPHQL_AUTH_MODE.AWS_IAM,
              });
              if (upd?.errors?.length) {
                console.error(
                  `[updateUser] FAILED to remove sub from '${g.name}'`,
                  upd.errors
                );
                throw new Error(
                  `Could not remove user from group '${g.name}': ${upd.errors[0]?.message}`
                );
              }
              console.log(
                `[updateUser] removed sub from AdminGroup '${g.name}' (users now ${nextUsers.length}, _version was ${version})`
              );
            }
          }
        }
      }

      // Verify: pull groups again and confirm the sub is ONLY in the target.
      if (newGroupName) {
        try {
          const userData2 = await get(username);
          const sub2 = userData2?.sub;
          if (sub2) {
            const verify: any = await graphqlTolerant({
              query: listAdminGroupsWithVersion,
              variables: { filter: { deleted: { eq: "0" } }, limit: 200 },
              authMode: session
                ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
                : GRAPHQL_AUTH_MODE.AWS_IAM,
            });
            const items: any[] = (verify?.data?.listAdminGroups?.items || []).filter(
              (g: any) => g && g.id
            );
            const stillIn = items
              .filter(
                (g: any) => Array.isArray(g.users) && g.users.includes(sub2)
              )
              .map((g: any) => g.name);
            console.log("[updateUser] groups containing this sub AFTER move:", stillIn);
            const unexpected = stillIn.filter(
              (n: string) => (n || "").toLowerCase() !== newGroupName
            );
            if (unexpected.length > 0) {
              console.warn(
                "[updateUser] sub is still in groups other than the target:",
                unexpected
              );
              showError(
                `User is still in: ${unexpected.join(", ")}. The remove step did not take effect — check console.`
              );
              return;
            }
          }
        } catch (e) {
          console.warn("[updateUser] post-move verification failed", e);
        }
      }

      showConfirm(
        "User updated. They must log out and log back in for new permissions to take effect."
      );
    } catch (err: Error | any) {
      console.error("[updateUser] failed", err);
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function deleteUser(username: string) {
    try {
      // Cleanup AdminGroup memberships before the Cognito user disappears,
      // because we need adminGetUser to translate username → sub.
      await removeUserSubFromAllAdminGroups(username);

      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.DELETE_USER,
        {},
        { username }
      );
      const result: any = await API.post(apiName, path, myInit);
      console.log("[deleteUser]", result?.message || "deleted");

      const updatedListing = listing.filter(
        (user: any) => user.Username !== username && user.id !== username
      );
      dispatch(setListing(updatedListing));

      showConfirm("User deleted successfully.");
    } catch (err: Error | any) {
      console.error("[deleteUser] failed", err);
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function disableUser(username: string) {
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.DISABLE_USER,
        {},
        {
          username,
        }
      );
      const { ...result } = await API.post(apiName, path, myInit);
      console.log(result.message);
      
      // Remove the disabled user from the listing
      const updatedListing = listing.filter(
        (user: any) => user.Username !== username && user.id !== username
      );
      dispatch(setListing(updatedListing));
      
      showConfirm("User disabled and removed from list successfully.");
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message);
    }
  }

  async function changePassword(oldPassword: string, newPassword: string) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const result = await Auth.changePassword(user, oldPassword, newPassword);
      showConfirm(result);
    } catch (err: Error | any) {
      console.log(err);
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "email",
      numeric: false,
      disablePadding: false,
      label: "Email",
    },
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
    },
    {
      id: "lastLoginAt",
      numeric: false,
      disablePadding: false,
      label: "Last Login",
    },
    {
      id: "lastActiveAt",
      numeric: false,
      disablePadding: false,
      label: "Last Active",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = [
    "email",
    "name",
    "lastLoginAt",
    "lastActiveAt",
  ];

  function isAdmin(session: any) {
    if (session && session["cognito:groups"]) {
      return (
        session["cognito:groups"].find((group: any) => group === "admin") !==
        undefined
      );
    }
  }

  async function isCurrentAuthUser(resourceUsername: string): Promise<boolean> {
    const { username } = await Auth.currentAuthenticatedUser();
    return username === resourceUsername;
  }

  const options: any[] = [];

  for (let option of listing) {
    options.push({ label: option.name, value: option.sub });
  }
  const api: any = {};

  api[`${listingName}Listing`] = listing;
  api[`${listingName}Options`] = options;
  api[`${listingName}SearchText`] = searchText;
  api[`${listingName}HeadCells`] = headCells;

  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Selected`] = selected;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchByGroup`] = byGroup;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = () => {};
  api[`${listingName}BulkTrash`] = () => {};
  api[`${listingName}Delete`] = () => {};
  api[`${listingName}ChangeListing`] = (listing: any[]) =>
    dispatch(setListing(listing));
  api[`${listingName}Search`] = (searchText: string) =>
    dispatch(setSearchText(searchText));
  api[`${listingName}Select`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));
  api["isAdmin"] = isAdmin;
  api["isCurrentAuthUser"] = isCurrentAuthUser;
  api["changePassword"] = changePassword;
  api[`${listingName}ListGroupsForUser`] = listGroupsForUser;
  api[`${listingName}ListAllGroups`] = listAllGroups;
  api[`addUserToGroup`] = addUserToGroup;
  api[`removeUserFromGroup`] = removeUserFromGroup;
  api[`${listingName}DisableUser`] = disableUser;
  api[`${listingName}DeleteUser`] = deleteUser;
  api[`${listingName}EnableUser`] = enableUser;
  api[`${listingName}CognitoGroups`] = COGNITO_GROUPS;

  return api;
};

export default useResource;
